/**
 * WVB-01 — Event Graph Visual (node-link diagram using @xyflow/react)
 * Smart filtering: excludes high-volume leaf nodes (DOCUMENT, TASK, STREAM_MEMBER)
 * by default to keep the visual manageable. Users can toggle them on.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactFlow, Background, Controls, MiniMap, useNodesState, useEdgesState, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { ExternalLink, Filter } from "lucide-react";
import { NODE_CONFIG, NODE_DEPTH, UPSTREAM_TYPES, navTarget, STATUS_STYLES } from "./EventGraphUtils";

const NODE_W = 190;
const H_GAP  = 60;
const DEPTH_H = 170;

// High-volume leaf types excluded by default
const LEAF_TYPES = new Set(["DOCUMENT", "TASK", "STREAM_MEMBER"]);

function computeLayout(nodes) {
  const byDepth = {};
  for (const n of nodes) {
    const d = NODE_DEPTH[n.node_type] ?? 6;
    if (!byDepth[d]) byDepth[d] = [];
    byDepth[d].push(n);
  }
  const pos = {};
  for (const [dStr, levelNodes] of Object.entries(byDepth)) {
    const d = parseInt(dStr, 10);
    const totalW = levelNodes.length * (NODE_W + H_GAP) - H_GAP;
    const startX = -totalW / 2;
    levelNodes.forEach((n, i) => {
      pos[n.id] = { x: startX + i * (NODE_W + H_GAP), y: d * DEPTH_H };
    });
  }
  return pos;
}

function buildFlowData(graphData, showLeaves) {
  const filteredNodes = showLeaves
    ? graphData.nodes
    : graphData.nodes.filter(n => !LEAF_TYPES.has(n.node_type));

  const filteredIds = new Set(filteredNodes.map(n => n.id));
  const filteredEdges = graphData.edges.filter(
    e => filteredIds.has(e.source) && filteredIds.has(e.target)
  );

  return { filteredNodes, filteredEdges };
}

function NodeLabel({ n, navigate }) {
  const cfg = NODE_CONFIG[n.node_type] || NODE_CONFIG.DOCUMENT;
  const url = navTarget(n.node_type, n.id);
  const stCls = n.status ? (STATUS_STYLES[n.status.toUpperCase()] || "bg-slate-50 text-slate-500") : "";

  return (
    <div className="text-center px-1" style={{ width: NODE_W - 28, minWidth: 140 }}>
      <div className="text-xs font-bold uppercase tracking-wide" style={{ color: cfg.text }}>
        {cfg.label}
      </div>
      <div className="text-sm font-semibold text-slate-800 leading-tight" title={n.label}
           style={{ wordBreak: "break-word" }}>
        {n.label.length > 24 ? n.label.slice(0, 22) + "…" : n.label}
      </div>
      {n.status && (
        <span className={`inline-block px-1.5 py-0 rounded text-xs font-medium mt-0.5 ${stCls}`}>
          {n.status}
        </span>
      )}
      {n.subtitle && (
        <div className="text-xs text-slate-400 mt-0.5">
          {n.subtitle.length > 20 ? n.subtitle.slice(0, 18) + "…" : n.subtitle}
        </div>
      )}
      {url && (
        <button
          className="text-xs text-indigo-500 hover:text-indigo-700 flex items-center justify-center gap-0.5 mt-0.5 mx-auto"
          onClick={e => { e.stopPropagation(); navigate(url); }}
          data-testid={`graph-nav-${n.id}`}
        >
          <ExternalLink className="w-2.5 h-2.5" />
          Open
        </button>
      )}
    </div>
  );
}

export default function EventGraphVisual({ graphData }) {
  const navigate = useNavigate();
  const [showLeaves, setShowLeaves] = useState(false);

  const { filteredNodes, filteredEdges } = useMemo(
    () => buildFlowData(graphData, showLeaves),
    [graphData, showLeaves]
  );

  const positions = useMemo(() => computeLayout(filteredNodes), [filteredNodes]);

  const initialNodes = useMemo(() => {
    return filteredNodes.map(n => {
      const cfg = NODE_CONFIG[n.node_type] || NODE_CONFIG.DOCUMENT;
      const pos = positions[n.id] || { x: 0, y: 0 };
      return {
        id: n.id,
        position: pos,
        data: { label: <NodeLabel n={n} navigate={navigate} /> },
        style: {
          background: cfg.bg,
          border: `2px solid ${cfg.border}`,
          borderRadius: 10,
          padding: "10px 10px",
          width: NODE_W,
          minHeight: 70,
        },
      };
    });
  }, [filteredNodes, positions, navigate]);

  const initialEdges = useMemo(() => {
    return filteredEdges.map(e => {
      const up = UPSTREAM_TYPES.has(e.edge_type);
      return {
        id: e.id,
        source: e.source,
        target: e.target,
        type: "smoothstep",
        label: e.label,
        animated: false,
        style: { stroke: up ? "#94a3b8" : "#6366f1", strokeWidth: 1.5 },
        markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12, color: up ? "#94a3b8" : "#6366f1" },
        labelStyle: { fontSize: 9, fill: "#94a3b8" },
        labelBgStyle: { fill: "#fff", opacity: 0.85 },
      };
    });
  }, [filteredEdges]);

  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  const hiddenCount = graphData.nodes.length - filteredNodes.length;

  return (
    <div data-testid="event-graph-visual">
      {/* Controls bar */}
      <div className="flex items-center gap-3 mb-3">
        <button
          onClick={() => setShowLeaves(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
            showLeaves
              ? "bg-indigo-50 text-indigo-700 border-indigo-200"
              : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
          }`}
          data-testid="graph-toggle-leaves"
        >
          <Filter className="w-3.5 h-3.5" />
          {showLeaves ? "Hide leaf nodes" : `Show all (+${hiddenCount} docs/tasks/members)`}
        </button>
        <span className="text-xs text-slate-400">
          Showing {filteredNodes.length} nodes · {filteredEdges.length} edges
        </span>
      </div>

      <div style={{ width: "100%", height: "560px" }} className="rounded-xl overflow-hidden border border-slate-200">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          fitView
          fitViewOptions={{ padding: 0.2, maxZoom: 1.0 }}
          minZoom={0.1}
          maxZoom={2}
          attributionPosition="bottom-right"
        >
          <Background color="#e2e8f0" gap={20} />
          <Controls position="bottom-left" />
          <MiniMap
            zoomable
            pannable
            style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
            position="bottom-right"
          />
        </ReactFlow>
      </div>
      <p className="text-xs text-slate-400 mt-2 text-center">
        Scroll to zoom · Drag to pan · Click Open on node cards to navigate
      </p>
    </div>
  );
}
